import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Button from '../components/ui/Button';
import { useQuote } from '../contexts/QuoteContext';
import { FaTrash } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';

const PageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
  min-height: 100vh;
  padding: 80px 0;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
  }
`;

const PageDescription = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.lightGray};
  width: 100%;
`;

const Form = styled.form`
  background-color: ${({ theme }) => theme.colors.darker};
  padding: 2rem;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  `;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  border: 1px solid ${({ theme }) => theme.colors.dark};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.light};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  border: 1px solid ${({ theme }) => theme.colors.dark};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.light};
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  border: 1px solid ${({ theme }) => theme.colors.dark};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.light};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
  }
`;

const SubmitContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const SuccessMessage = styled.div`
  background-color: rgba(39, 174, 96, 0.2);
  border: 1px solid #27ae60;
  color: #2ecc71;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: rgba(231, 76, 60, 0.2);
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: 2rem;
  text-align: center;
`;

const QuoteItemsSection = styled.div`
  margin-bottom: 2rem;
`;

const QuoteItemsTitle = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const QuoteItemsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const QuoteItem = styled.li`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
`;

const QuoteItemName = styled.span`
  color: ${({ theme }) => theme.colors.light};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.25rem;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    opacity: 0.8;
  }
`;

const EmptyQuoteMessage = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  font-style: italic;
  margin-bottom: 1.5rem;
`;

interface FormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  productType: string;
  quantity: string;
  projectDescription: string;
}

const RequestQuotePage: React.FC = () => {
  const { quoteItems, removeFromQuote, clearQuote } = useQuote();
  
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    productType: '',
    quantity: '',
    projectDescription: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Update project description when quote items change
  useEffect(() => {
    if (quoteItems.length > 0) {
      // Save any existing custom text that follows our template pattern
      let customText = '';
      if (formData.projectDescription.includes('Additional project details:')) {
        const splitText = formData.projectDescription.split('Additional project details:');
        if (splitText.length > 1) {
          customText = splitText[1].trim();
        }
      }
      
      const productNames = quoteItems.map(item => item.name).join('\n- ');
      const newDescription = `Products in Quote:\n\n- ${productNames}\n\nAdditional project details:${customText ? '\n' + customText : '\n'}`;
      
      setFormData(prevState => ({
        ...prevState,
        projectDescription: newDescription
      }));
    } else {
      // When no products, remove the product template but keep custom text
      if (formData.projectDescription.includes('Additional project details:')) {
        const splitText = formData.projectDescription.split('Additional project details:');
        if (splitText.length > 1) {
          const customText = splitText[1].trim();
          // Only keep the custom text the user entered
          setFormData(prevState => ({
            ...prevState,
            projectDescription: customText
          }));
        } else {
          // No custom text after the template, clear the field
          setFormData(prevState => ({
            ...prevState,
            projectDescription: ''
          }));
        }
      }
    }
  }, [quoteItems, formData.projectDescription]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const quotedProducts = quoteItems.map(item => item.name).join(', ');
      
      const response = await fetch('/api/quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          productType: formData.productType,
          quantity: formData.quantity,
          projectDescription: formData.projectDescription,
          quotedProducts: quotedProducts
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Network response was not ok');
      }
      
    setFormData({
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      productType: '',
      quantity: '',
      projectDescription: ''
    });
    
      clearQuote();
      
      setSubmitted(true);
      
    window.scrollTo(0, 0);
    } catch (err) {
      setError('There was a problem sending your quote request. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const pageTitle = 'Request a Custom Quote | ElectroCable Wire & Cable';
  const pageDescription = 'Ready to start your project? Submit your wire and cable requirements to ElectroCable for a fast, accurate, and customized quote. Add products to your list and provide your project details to get started.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const quotePageUrl = `${siteUrl}/request-quote`;
  const ogImageUrl = `${siteUrl}/images/request-quote-og-image.jpg`; // Replace with a relevant OG image

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */} 
        <meta property="og:type" content="website" />
        <meta property="og:url" content={quotePageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* Twitter */} 
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={quotePageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />
      </Helmet>

      <ContentContainer>
        <PageTitle>Request a Quote</PageTitle>
        <PageDescription>
          Fill out the form below with your project details, and our team will provide 
          you with a customized quote for your electrical cable needs.
        </PageDescription>
        
        {submitted && (
          <SuccessMessage>
            <h3>Quote Request Received!</h3>
            <p>Thank you for your request. Our sales team will contact you within 24 hours.</p>
          </SuccessMessage>
        )}
        
        {error && (
          <ErrorMessage>
            <h3>Submission Error</h3>
            <p>{error}</p>
          </ErrorMessage>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <InputLabel htmlFor="firstName">First Name *</InputLabel>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="lastName">Last Name *</InputLabel>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <InputLabel htmlFor="company">Company Name *</InputLabel>
            <Input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormRow>
            <FormGroup>
              <InputLabel htmlFor="email">Email Address *</InputLabel>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="phone">Phone Number *</InputLabel>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormRow>
          
          <FormRow>
            <FormGroup>
              <InputLabel htmlFor="productType">Product Type</InputLabel>
              <Select
                id="productType"
                name="productType"
                value={formData.productType}
                onChange={handleChange}
              >
                <option value="">Select a product type</option>
                <option value="bare-aluminum">Bare Aluminum</option>
                <option value="service-drop">Service Drop</option>
                <option value="600v-ud">600V UD</option>
                <option value="medium-voltage">Medium Voltage Solutions</option>
                <option value="custom">Custom Solution</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="quantity">Estimated Quantity</InputLabel>
              <Input
                type="text"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="e.g., 500 ft, 10,000 meters, etc."
              />
            </FormGroup>
          </FormRow>
          
          <QuoteItemsSection>
            <QuoteItemsTitle>Products Included in Your Quote</QuoteItemsTitle>
            {quoteItems.length > 0 ? (
              <QuoteItemsList>
                {quoteItems.map((item) => (
                  <QuoteItem key={item.id}>
                    <QuoteItemName>{item.name}</QuoteItemName>
                    <RemoveButton 
                      onClick={(e) => {
                        e.preventDefault();
                        removeFromQuote(item.id);
                      }}
                      title="Remove from quote"
                    >
                      <IconWrapper icon={FaTrash} size={16} />
                    </RemoveButton>
                  </QuoteItem>
                ))}
              </QuoteItemsList>
            ) : (
              <EmptyQuoteMessage>
                No products added to your quote yet. Browse our products and add items to your quote.
              </EmptyQuoteMessage>
            )}
          </QuoteItemsSection>
          
          <FormGroup>
            <InputLabel htmlFor="projectDescription">Project Description</InputLabel>
            <TextArea
              id="projectDescription"
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleChange}
              placeholder="Please describe your project and specific requirements..."
            />
          </FormGroup>
          
          <SubmitContainer>
            <Button 
              variant="primary" 
              size="large" 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          </SubmitContainer>
        </Form>
      </ContentContainer>
    </PageContainer>
  );
};

export default RequestQuotePage; 